import React from 'react';

import {Colors, Drawer, Text, Title} from '@optimaxdev/design-system';
import {
    Icon,
    KlarnaBadge as KlarnaIcon,
    PaypalTransparent as PayPalIcon,
    Affirm as AffirmIcon,
} from '@optimaxdev/icons';

import {connect} from 'react-redux';

import {PayOverTimeLocations, PayLaterMethods} from 'constants/payOverTime';
import {getGrandTotal} from 'desktop/pages/checkout/store/selectors/orderReviewTotals/orderReviewTotals';

import {ApplicationStoreType} from 'desktop/store';

import {ButtonChooser} from './learnMoreButtons/buttonChooser';

import s from './payOverTimePopup.scss';

export type PropsType = {
    isOpened: boolean;
    closePopup: () => void;
    location: PayOverTimeLocations;
    checkoutTotal: number;
    total: number;
};

/**
 * PayOverTime popup content
 * @param {PropsType} - props
 * @returns {React.ReactNode} - component
 */
export const PayOverTimePopup: React.FC<PropsType> = ({
    checkoutTotal,
    closePopup,
    isOpened,
    location,
    total,
}) => {
    const price = location === PayOverTimeLocations.CHECKOUT_REBATE ? checkoutTotal : total;

    const renderTitle = (text: string) => {
        return (
            <Text
                as="p"
                color={Colors.blueCharcoal}
                lineHeight="24px"
                margin="16px 0 0 0"
                size="16px"
                bold
            >
                {text}
            </Text>
        );
    };
    const renderContent = (text: string, payLaterMethod?: PayLaterMethods) => {
        return (
            <Text
                as="p"
                color={Colors.limedSpruce}
                lineHeight="24px"
                margin={payLaterMethod ? '6px 0 0 0' : '8px 0 0 0'}
                size="16px"
            >
                {text}&nbsp;
                {payLaterMethod && (
                    <ButtonChooser
                        amount={price}
                        location={location}
                        payLaterMethod={payLaterMethod}
                    />
                )}
            </Text>
        );
    };

    return (
        <Drawer
            header={
                <Title color={Colors.blueCharcoal} fontSize="16px" level={3} lineHeight="19px">
                    Shop now. Pay later.
                </Title>
            }
            isOpened={isOpened}
            onClick={closePopup}
        >
            <div className={s.wrapper}>
                <div className={s.subtitle}>
                    <Text as="p" color={Colors.limedSpruce} lineHeight="24px" size="16px">
                        Select Klarna, PayPal, or Affirm as your payment method at checkout to enjoy
                        a flexible payment plan tailored to your needs.
                    </Text>
                </div>
                <div className={s.container}>
                    <section className={s.payLaterMethod}>
                        <Icon icon={KlarnaIcon} width={64} />
                        {renderTitle('Pay in 4 or Pay over time.')}
                        {renderContent(
                            'Split the cost into smaller payments.',
                            PayLaterMethods.KLARNA_PAY_LATER,
                        )}
                    </section>

                    <section className={s.payLaterMethod}>
                        <Icon height={26} icon={PayPalIcon} width={87} />
                        {renderTitle('Pay in 4.')}
                        {renderContent(
                            'Split your purchase into 4 interest-free, bi-weekly payments. No late fees.',
                            PayLaterMethods.PAY_LATER_SHORT_TERM,
                        )}

                        {renderTitle('Pay Monthly.')}
                        {renderContent(
                            'Enjoy the flexibility of 6,12, or 24-month payment plans, $0 down. No sign-up or late fees.',
                            PayLaterMethods.PAY_LATER_LONG_TERM,
                        )}

                        {renderTitle('PayPal credit.')}
                        {renderContent(
                            'Your own always-on credit line.',
                            PayLaterMethods.PAYPAL_CREDIT_NO_INTEREST,
                        )}
                    </section>

                    <section className={s.payLaterMethod}>
                        <Icon height={34} icon={AffirmIcon} />
                        {renderContent(
                            'Select Amazon Pay or Google Pay at checkout to use Affirm’s payment plans.',
                        )}
                    </section>
                </div>
            </div>
        </Drawer>
    );
};

/**
 * Map state to props
 * @param {ApplicationStoreType} store - redux store
 * @returns {PropsType} props
 */
const mapStateToProps = (store: ApplicationStoreType) => ({
    checkoutTotal: getGrandTotal(store).price,
    total: Number(store.cart.totals.total),
});

export const PayOverTimePopupConnected = connect(mapStateToProps)(PayOverTimePopup);
